export const PAYMENT_HUB_MANAGER_ROLES = ['superadmin', 'payment-manager'] as const;
export const PAYMENT_HUB_AGENT_ROLES = ['payment-agent'] as const;
export const EXEC_PAYMENT_ROLE_SLUGS = ['exec-payment', 'exec_payment', 'execpayment'] as const;
export const NOTICE_ISSUER_IMPORT_ROUTE = '/execution/iban-jm-notice';
export const PAYMENT_NOTICE_IMPORT_ROUTE = '/payment/iban-jm-notice';
export const EXECUTION_NOTICE_BATCH_ROUTE_PREFIX = '/execution/notice-batch';
export const PAYMENT_NOTICE_BATCH_ROUTE_PREFIX = '/payment/notice-batch';

export type PaymentAccessMode = 'full' | 'notice-only' | 'none';

function normalizeRoleSlug(role: string): string {
  return String(role ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');
}

/** Coerce session/API group payload to a string array (keeps DB slug casing). */
export function coerceUserGroups(groups: unknown): string[] {
  if (Array.isArray(groups)) {
    return groups.map((role) => String(role ?? '').trim()).filter(Boolean);
  }
  if (groups && typeof groups === 'object') {
    return Object.values(groups as Record<string, unknown>)
      .map((role) => String(role ?? '').trim())
      .filter(Boolean);
  }
  if (typeof groups === 'string') {
    return groups
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean);
  }
  return [];
}

export function normalizeUserRoles(groups: unknown): string[] {
  return coerceUserGroups(groups).map((role) => normalizeRoleSlug(role));
}

export function hasExecPaymentRole(groups: string[]): boolean {
  const normalized = groups.map(normalizeRoleSlug);
  return normalized.some((role) =>
    (EXEC_PAYMENT_ROLE_SLUGS as readonly string[]).includes(role),
  );
}

export function hasPaymentHubManagerAccess(groups: string[]): boolean {
  const normalized = groups.map(normalizeRoleSlug);
  return normalized.some((role) =>
    (PAYMENT_HUB_MANAGER_ROLES as readonly string[]).includes(role),
  );
}

export function hasPaymentAgentAccess(groups: string[]): boolean {
  const normalized = groups.map(normalizeRoleSlug);
  return normalized.some((role) => (PAYMENT_HUB_AGENT_ROLES as readonly string[]).includes(role));
}

/** Standard execution roles (full execution module, not notice-import only). */
export function hasFullExecutionAccess(groups: string[]): boolean {
  const normalized = groups.map(normalizeRoleSlug);
  return (
    normalized.includes('admin') ||
    normalized.includes('superadmin') ||
    normalized.includes('exec') ||
    normalized.includes('m-exec')
  );
}

/**
 * exec-payment alone is notice-import only on execution/payment.
 * When combined with exec / m_exec, execution module stays fully available.
 */
export function getPaymentAccessMode(groups: unknown): PaymentAccessMode {
  const roles = normalizeUserRoles(groups);
  if (!roles.length) {
    return 'none';
  }
  if (hasPaymentHubManagerAccess(roles)) {
    return 'full';
  }
  if (hasExecPaymentRole(roles) && !hasFullExecutionAccess(roles)) {
    return 'notice-only';
  }
  if (hasPaymentAgentAccess(roles)) {
    return 'full';
  }
  return 'none';
}

export function hasPaymentHubFullAccess(groups: string[]): boolean {
  return getPaymentAccessMode(groups) === 'full';
}

export function isExecPaymentOnlyUser(groups: unknown): boolean {
  return getPaymentAccessMode(groups) === 'notice-only';
}

export function canAccessNoticeIssuerImportRoute(groups: unknown): boolean {
  const roles = normalizeUserRoles(groups);
  return roles.includes('superadmin') || roles.includes('m-exec');
}

export function canAccessPaymentNoticeImportRoute(groups: unknown): boolean {
  const roles = normalizeUserRoles(groups);
  return hasPaymentHubFullAccess(roles) || roles.includes('admin');
}

export function isNoticeBatchRoute(url: string): boolean {
  return (
    url.includes(`${EXECUTION_NOTICE_BATCH_ROUTE_PREFIX}/`) ||
    url.includes(`${PAYMENT_NOTICE_BATCH_ROUTE_PREFIX}/`)
  );
}

/** Shared notice batch detail page — payment validators and execution (per-batch exec-in-charge enforced in UI). */
export function canAccessNoticeBatchRoute(groups: unknown): boolean {
  if (canAccessPaymentNoticeImportRoute(groups)) {
    return true;
  }
  const roles = normalizeUserRoles(groups);
  if (roles.includes('superadmin') || roles.includes('m-exec')) {
    return true;
  }
  // Exec agents may open a batch when they are exec in charge of the deal (checked in the batch component).
  return roles.includes('exec');
}

const PAYMENT_SUPERADMIN_ONLY_ROUTE_PREFIXES = [
  '/payment/debit-account-requests',
  '/payment/activities',
] as const;

export function hasPaymentSuperAdminAccess(groups: unknown): boolean {
  return normalizeUserRoles(groups).includes('superadmin');
}

/** IBAN JM hub listing and debit/activity admin pages — superadmin only. */
export function isPaymentSuperAdminOnlyRoute(url: string): boolean {
  const path = (url.split('?')[0] || '').replace(/\/+$/, '') || '/';
  if (
    (PAYMENT_SUPERADMIN_ONLY_ROUTE_PREFIXES as readonly string[]).some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
  ) {
    return true;
  }
  if (path === '/payment/iban-jm' || path.startsWith('/payment/iban-jm/')) {
    return !path.startsWith('/payment/iban-jm-notice');
  }
  return false;
}

export function canAccessPaymentRoute(groups: unknown, url: string): boolean {
  if (isNoticeBatchRoute(url) && canAccessNoticeBatchRoute(groups)) {
    return true;
  }
  if (isPaymentSuperAdminOnlyRoute(url)) {
    return hasPaymentSuperAdminAccess(groups);
  }
  return getPaymentAccessMode(groups) === 'full';
}

/** WIRE CSV export download — payment agents and superadmin only. */
export function canDownloadNoticeCsv(groups: unknown): boolean {
  const roles = normalizeUserRoles(groups);
  return roles.includes('payment-agent') || roles.includes('superadmin');
}

export function canAccessExecutionRoute(groups: unknown, url: string): boolean {
  const roles = normalizeUserRoles(groups);
  if (!roles.length) {
    return false;
  }
  if (url.includes(NOTICE_ISSUER_IMPORT_ROUTE)) {
    return canAccessNoticeIssuerImportRoute(groups);
  }
  if (isNoticeBatchRoute(url)) {
    return canAccessNoticeBatchRoute(groups);
  }
  if (roles.includes('admin') || roles.includes('superadmin') || roles.includes('exec') || roles.includes('m-exec')) {
    return true;
  }
  return false;
}

/** Home dashboard route for the current user's primary role. */
export function getRoleDashboardRoute(groups: unknown): string {
  const roles = normalizeUserRoles(groups);
  if (roles.includes('admin') || roles.includes('superadmin')) {
    return '/admin/admin-dashboard';
  }
  if (roles.includes('exec') || roles.includes('m-exec')) {
    return '/execution/execution-dashboard';
  }
  if (roles.includes('kyc-bo') || roles.includes('ext-kyc-bo') || roles.includes('kyc-validator')) {
    return '/kyc/kyc-dashboard';
  }
  if (roles.includes('billing')) {
    return '/billing/billing-dashboard';
  }
  if (roles.includes('busdev') || roles.includes('collab') || roles.includes('m-com')) {
    return '/crm/crm-dashboard';
  }
  if (roles.includes('payment-agent') || roles.includes('payment-manager')) {
    return '/payment/payment-dashboard';
  }
  if (roles.includes('cde-manager') || roles.includes('cde-admin')) {
    return '/crm/cde-manager';
  }
  return '/crm/crm-dashboard';
}

export interface NoticeBatchBreadcrumb {
  label: string;
  route?: string;
}

/** Middle crumb on notice batch detail — hidden when the user has no notice listing access. */
export function getNoticeBatchPaymentNoticesBreadcrumb(groups: unknown): NoticeBatchBreadcrumb | null {
  if (canAccessNoticeIssuerImportRoute(groups)) {
    return { label: 'Payment notices', route: NOTICE_ISSUER_IMPORT_ROUTE };
  }
  const roles = normalizeUserRoles(groups);
  if (hasPaymentAgentAccess(roles) || roles.includes('payment-manager')) {
    return { label: 'Payment notices', route: PAYMENT_NOTICE_IMPORT_ROUTE };
  }
  return null;
}
